import { effectiveStartWeek, termWeekCount, weekInEnrollmentWindow, type LessonWindowSource } from './dateUtils';
import { classRunsInWeek, type ClassWeeksRow } from './classWeeks';

/**
 * The enrolments that attend ONE occurrence of a class (mid-term start week, Phase 4).
 * A student is on the roll only from their effective start week: max(class start,
 * their own). Everything goes through `weekInEnrollmentWindow` — never re-derive it.
 *
 * A week a plain class does not run (a fortnightly class's off week, a band's non-rehearsal
 * week) has NO roll — but only when the caller passes the class's `termId` and
 * `LessonTermWeeks`: like every other input here, their absence fails open (2026-10-15).
 *
 * Fails OPEN: with no occurrence week (null, or a non-positive placeholder such as the
 * `0` the calendar uses for "no week") or no term dates the list is returned whole,
 * because a missing input must never hide a child from their teacher.
 */
export function enrollmentsForOccurrence<E extends { startWeek?: number | null }>(
  lesson: LessonWindowSource & {
    Term?: { startDate: string; endDate: string } | null;
    termId?: string;
    LessonType?: { type: string } | null;
    LessonTermWeeks?: ReadonlyArray<ClassWeeksRow> | null;
  },
  enrollments: readonly E[] | undefined | null,
  weekNumber: number | undefined | null
): E[] {
  const list = enrollments ?? [];
  if (weekNumber == null || weekNumber < 1 || !lesson.Term?.startDate || !lesson.Term?.endDate) return [...list];
  const termWeeks = termWeekCount(lesson.Term.startDate, lesson.Term.endDate);
  if (
    lesson.termId &&
    lesson.LessonTermWeeks &&
    lesson.LessonType?.type !== 'HYBRID' &&
    !classRunsInWeek(
      { ...lesson, termId: lesson.termId, LessonType: lesson.LessonType ?? null, LessonTermWeeks: lesson.LessonTermWeeks },
      weekNumber,
      termWeeks
    )
  ) {
    return [];
  }
  return list.filter((enrollment) => weekInEnrollmentWindow(weekNumber, lesson, enrollment, termWeeks));
}

/**
 * The week a student STARTS, when that is after `week` — else null. Drives the
 * "Starts week 7" chip that keeps an admin from pre-booking a clinic for a week the
 * child has not joined yet (the server refuses it too). Fails open: a student with no
 * matching active enrolment, or a missing week, is never reported as not started.
 */
export function startsAfterWeek(
  lesson: LessonWindowSource | null | undefined,
  enrollments: readonly { studentId: string; isActive?: boolean; startWeek?: number | null }[] | undefined | null,
  studentId: string,
  week: number | null | undefined
): number | null {
  if (!lesson || week == null || week < 1) return null;
  const enrollment = (enrollments ?? []).find((e) => e.studentId === studentId && e.isActive !== false);
  if (!enrollment) return null;
  const start = effectiveStartWeek(lesson, enrollment);
  return week < start ? start : null;
}
